import { AppointmentParticipantResponse } from "@9amhealth/openapi";
import { useBloc, useBlocNext } from "@blac/react";
import React, { FC, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { KnownProgram } from "src/constants/programs";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import AppointmentsBloc, {
  AppointmentSanityDetails
} from "src/state/AppointmentsBloc/AppointmentsBloc";
import TaskManagementBloc from "src/state/TaskManagementBloc/TaskManagementBloc";
import { toast } from "src/state/state";
import { AppPopup, AppQueryPopupsController } from "../../AppQueryPopupsBloc";
import InDialog from "src/ui/components/InDialog/InDialog";
import useGoToOrBack from "src/ui/hooks/useGoToOrBack";
import Loader from "../../../Loader/Loader";
import Translate from "../../../Translate/Translate";
import AppointmentEventContent from "./AppointmentEventContent";
import AppointmentHeroBanner from "./AppointmentHeroBanner";
import LabsHint from "./LabsHint";
import {
  Action,
  ActionContainer,
  ActionsWrap,
  AppointmentContent,
  EventDateTime,
  EventTitle,
  JoiningInstructions,
  Wrap
} from "./AppointmentDialog.styled";

const AppointmentDialog: FC<{
  returnUrl: string;
}> = ({ returnUrl }) => {
  const navigate = useGoToOrBack();

  const onClose = () => {
    AppQueryPopupsController.closePopup();
    navigate(returnUrl, {
      multiBack: true,
      replace: true
    });
  };

  const [appointmentDetails, setAppointmentDetails] =
    React.useState<AppointmentSanityDetails>();
  const [loading, setLoading] = React.useState<boolean>(false);

  const [
    { scheduledAppointments },
    {
      getAppointment,
      createAppointmentConfig,
      appointmentRequiresLabsHint,
      getAppointmentTime
    }
  ] = useBloc(AppointmentsBloc);

  const [, { hasIncompleteLabTask, loadProgramTasks }] = useBlocNext(
    TaskManagementBloc,
    {
      dependencySelector: (state) => [state.tasks]
    }
  );

  const [searchParams] = useSearchParams();
  const appointmentId = searchParams.get("id") ?? "";
  const appointment = getAppointment(appointmentId, scheduledAppointments);

  const { start, end, location } = appointment ?? {};

  const hostParticipant = useMemo(() => {
    return appointment?.participants.find(
      (p) => p.role === AppointmentParticipantResponse.role.STAFF
    );
  }, [appointment]);

  const hostUserId = hostParticipant?.userId;

  const hostLabel = useMemo(() => {
    if (!hostParticipant?.displayName) {
      return undefined;
    }

    const { displayName, displayRole } = hostParticipant;
    return displayRole ? `${displayName}, ${displayRole}` : displayName;
  }, [hostParticipant]);

  const { weekday, timeZone, startTime, endTime } = getAppointmentTime(
    start,
    end
  );

  const hasZoomLink = location?.includes("zoom");

  useEffect(() => {
    if (!appointment) {
      return;
    }

    const fetchCmsDetails = async () => {
      setLoading(true);

      try {
        const data = await AppointmentsBloc.getAppointmentCmsDetails(
          appointment.type
        );

        setAppointmentDetails(data);
      } catch (error) {
        toast.error("error_appointment_details_load_failed");

        reportErrorSentry(error);
      } finally {
        setLoading(false);
      }
    };

    void fetchCmsDetails();
  }, [appointment]);

  useEffect(() => {
    // The initial lab order task lives under the ONBOARDING program, so it
    // must be loaded explicitly rather than relying on some other screen
    // having already fetched it.
    void loadProgramTasks(KnownProgram.ONBOARDING);
  }, []);

  if (!appointment) {
    return null;
  }
  const config = createAppointmentConfig(appointment);

  const showLabsHint =
    appointmentRequiresLabsHint(appointment) && hasIncompleteLabTask;

  const handleOpenLabs = () => {
    AppQueryPopupsController.openPopup(AppPopup.initialLab);
  };

  return (
    <InDialog
      width="min(calc(100% - 4rem), 120rem)"
      height="min(calc(100% - 4rem), 70rem)"
      simple
      backdropDismiss={false}
      title={appointment.title || translate("appointment.popup.title")}
      onClose={onClose}
      returnUrl={returnUrl}
      noPadding
    >
      {loading && <Loader fixed active />}
      <AppointmentContent>
        <AppointmentHeroBanner
          hostUserId={hostUserId}
          avatarUrl={appointmentDetails?.avatar?.url}
          avatarAlt={appointmentDetails?.eventName}
        />

        <Wrap>
          <EventTitle className="as-h4">
            {
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
              appointmentDetails?.eventName || appointment.title
            }
            {hostLabel && (
              <>
                {" "}
                <Translate
                  msg="appointment.with"
                  variables={{ name: hostLabel }}
                />
              </>
            )}
          </EventTitle>
          <EventDateTime className="as-body2 color-c-80">
            <Translate
              msg="appointment.eventDateTime"
              variables={{
                context: "detail",
                weekday,
                startTime,
                endTime,
                timeZone
              }}
            />
          </EventDateTime>
          {hasZoomLink && appointmentDetails && (
            <JoiningInstructions className="body1 color-c-80">
              {appointmentDetails.joiningInstructions}
            </JoiningInstructions>
          )}

          <ActionsWrap>
            {config.detailActions.map((action, index) => (
              <Action
                onClick={(): void => action.onClick()}
                key={index}
                className="appointment-action"
              >
                <ActionContainer>{action.icon}</ActionContainer>
                <span>
                  <Translate
                    msg={action.label}
                    variables={{ context: "detail" }}
                  />
                </span>
              </Action>
            ))}
          </ActionsWrap>

          {showLabsHint && <LabsHint onOpenLabs={handleOpenLabs} />}

          {appointmentDetails?.eventContent && (
            <AppointmentEventContent
              sections={appointmentDetails.eventContent}
            />
          )}
        </Wrap>
      </AppointmentContent>
    </InDialog>
  );
};

export default AppointmentDialog;
